/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { surveyApi } from '@/services/adminApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Code } from 'lucide-react';
import type { Survey, QuestionType } from '@/types/admin';

const questionOptionSchema = z.object({
  id: z.string(),
  text: z.string().min(1, 'Option text is required'),
  value: z.string().optional(),
  order: z.number(),
});

const conditionalRuleSchema = z.object({
  condition: z.string().optional(),
  action: z.string(),
  targetQuestionId: z.string().optional(),
}).passthrough();

const questionSchema = z.object({
  id: z.string(),
  text: z.string().min(1, 'Question text is required'),
  type: z.enum([
    'SINGLE_CHOICE',
    'MULTIPLE_CHOICE',
    'TEXT_SHORT',
    'TEXT_LONG',
    'NUMBER',
    'EMAIL',
    'DATE',
    'TIME',
    'RATING_SCALE',
    'SLIDER',
    'DROPDOWN',
    'FILE_UPLOAD',
    'BOOLEAN',
    'HEADING',
    'BANNER',
  ]),
  required: z.boolean().default(false),
  order: z.number().min(1),
  helpText: z.string().optional(),
  options: z.array(questionOptionSchema).optional(),
  conditionalRules: z.array(conditionalRuleSchema).optional(),
});

const surveySchema = z.object({
  surveyId: z
    .string()
    .min(1, 'Survey ID is required')
    .regex(/^SVY-[a-zA-Z0-9_-]+$/, 'Survey ID must match pattern: SVY-[a-zA-Z0-9_-]+'),
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  type: z.enum(['FEEDBACK', 'ASSESSMENT', 'QUIZ', 'POLL', 'RESEARCH', 'ONBOARDING', 'OTHER']),
  category: z.enum([
    'GENERAL',
    'USER_ONBOARDING',
    'PRODUCT_FEEDBACK',
    'CUSTOMER_SATISFACTION',
    'EMPLOYEE_FEEDBACK',
  ]),
  tags: z.string().optional(),
  active: z.boolean().default(true),
  questions: z.array(questionSchema).min(1, 'At least one question is required'),
  routingRules: z.array(z.object({
    condition: z.string().optional(),
    action: z.string(),
    targetQuestionId: z.string().optional(),
  }).passthrough()).optional(),
  configuration: z
    .object({
      allowBackNavigation: z.boolean().default(true),
      showProgressIndicator: z.boolean().default(true),
      randomizeQuestions: z.boolean().default(false),
      randomizeOptions: z.boolean().default(false),
      autoSave: z.boolean().default(false),
      allowMultipleResponses: z.boolean().default(false),
      timeLimit: z.number().optional(),
    })
    .optional(),
});

type SurveyFormData = z.infer<typeof surveySchema>;

export const SurveyFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonEditorValue, setJsonEditorValue] = useState('');
  const [jsonEditorError, setJsonEditorError] = useState<string | null>(null);

  const form = useForm<SurveyFormData>({
    resolver: zodResolver(surveySchema) as any,
    defaultValues: {
      surveyId: '',
      title: '',
      description: '',
      type: 'ONBOARDING',
      category: 'USER_ONBOARDING',
      tags: '',
      active: true,
      questions: [
        {
          id: `q-${Date.now()}`,
          text: '',
          type: 'TEXT_SHORT',
          required: false,
          order: 1,
          helpText: '',
          options: [],
          conditionalRules: [],
        },
      ],
      routingRules: [],
      configuration: {
        allowBackNavigation: true,
        showProgressIndicator: true,
        randomizeQuestions: false,
        randomizeOptions: false,
        autoSave: false,
        allowMultipleResponses: false,
      },
    },
  });

  const { fields, append, swap } = useFieldArray({
    control: form.control,
    name: 'questions',
  });

  // Fetch survey data for edit mode
  const {
    data: _surveyData,
    isLoading: isLoadingSurvey,
    error: surveyError,
  } = useEntity<Survey>({
    fetchFn: (surveyId: string) => surveyApi.getById(surveyId),
    entityId: id,
    entityName: 'survey',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      const formData = {
        surveyId: data.surveyId,
        title: data.title,
        description: data.description || '',
        type: data.type,
        category: data.category,
        tags: data.tags?.join(', ') || '',
        active: data.active ?? true,
        questions:
          data.questions?.map((q, idx) => ({
            id: q.id,
            text: q.text,
            type: q.type,
            required: q.required ?? false,
            order: q.order || idx + 1,
            helpText: q.helpText || '',
            options:
              q.options?.map((opt, optIdx) => ({
                id: opt.id,
                text: opt.text,
                value: opt.value,
                order: opt.order || optIdx + 1,
              })) || [],
            conditionalRules: Array.isArray(q.conditionalRules)
              ? (q.conditionalRules as SurveyFormData['questions'][0]['conditionalRules'])
              : [],
          })) || [],
        routingRules: (Array.isArray(data.routingRules) ? data.routingRules : []) as SurveyFormData['routingRules'],
        configuration: {
          allowBackNavigation: data.configuration?.allowBackNavigation ?? true,
          showProgressIndicator: data.configuration?.showProgressIndicator ?? true,
          randomizeQuestions: data.configuration?.randomizeQuestions ?? false,
          randomizeOptions: data.configuration?.randomizeOptions ?? false,
          autoSave: data.configuration?.autoSave ?? false,
          allowMultipleResponses: data.configuration?.allowMultipleResponses ?? false,
        },
      } as SurveyFormData;
      form.reset(formData);
    },
    onError: () => {
      navigate('/admin/surveys');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: SurveyFormData) => {
      // Reorder questions based on order field
      const sortedQuestions = [...data.questions].sort((a, b) => a.order - b.order);

      const payload: Omit<Survey, 'id' | 'createdAt' | 'updatedAt'> = {
        surveyId: data.surveyId,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        active: data.active,
        status: 'DRAFT',
        questions: sortedQuestions.map((q) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          required: q.required,
          order: q.order,
          helpText: q.helpText,
          options: q.options || [],
          conditionalRules: q.conditionalRules ?? [],
        })),
        routingRules: (data.routingRules ?? []) as Survey['routingRules'],
        configuration: data.configuration,
      };
      return await surveyApi.create(payload);
    },
    entityName: 'survey',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/surveys');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SurveyFormData) => {
      if (!id) throw new Error('Survey ID is required');
      // Reorder questions based on order field
      const sortedQuestions = [...data.questions].sort((a, b) => a.order - b.order);

      const payload: Omit<Survey, 'id' | 'createdAt' | 'updatedAt'> = {
        surveyId: data.surveyId,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        active: data.active,
        status: 'DRAFT',
        questions: sortedQuestions.map((q) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          required: q.required,
          order: q.order,
          helpText: q.helpText,
          options: q.options || [],
          conditionalRules: q.conditionalRules ?? [],
        })),
        routingRules: (data.routingRules ?? []) as Survey['routingRules'],
        configuration: data.configuration,
      };
      return await surveyApi.update(id, payload);
    },
    entityName: 'survey',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/surveys');
    },
  });

  const onSubmit = async (data: SurveyFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingSurvey;
  const mutation = isEdit ? updateMutation : createMutation;

  const openJsonEditor = useCallback(() => {
    setJsonEditorError(null);
    try {
      const values = form.getValues();
      setJsonEditorValue(JSON.stringify(values, null, 2));
      setJsonEditorOpen(true);
    } catch (e) {
      setJsonEditorError(e instanceof Error ? e.message : 'Failed to serialize form');
    }
  }, [form]);

  const applyJsonEditor = useCallback(() => {
    setJsonEditorError(null);
    try {
      const parsed = JSON.parse(jsonEditorValue) as SurveyFormData;
      if (!parsed.questions || !Array.isArray(parsed.questions) || parsed.questions.length === 0) {
        setJsonEditorError('Survey must have at least one question.');
        return;
      }
      form.reset(parsed);
      setJsonEditorOpen(false);
    } catch (e) {
      setJsonEditorError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  }, [jsonEditorValue, form]);

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading survey..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && surveyError) {
    return <ErrorState error={surveyError} onRetry={() => window.location.reload()} />;
  }

  const addQuestion = () => {
    const newOrder = fields.length + 1;
    append({
      id: `q-${Date.now()}-${Math.random()}`,
      text: '',
      type: 'TEXT_SHORT',
      required: false,
      order: newOrder,
      helpText: '',
      options: [],
      conditionalRules: [],
    });
  };

  /** Returns true if a rule references the given question id (in condition or targetQuestionId). */
  const ruleReferencesQuestionId = (
    rule: { condition?: string; targetQuestionId?: string },
    questionId: string
  ): boolean => {
    if (rule.targetQuestionId === questionId) return true;
    if (typeof rule.condition === 'string' && rule.condition.includes(questionId)) return true;
    return false;
  };

  /** Remove question at index and cascade: renumber order, remove refs in other questions' conditionalRules and in routingRules. */
  const removeQuestion = (index: number) => {
    const questions = form.getValues('questions');
    if (index < 0 || index >= questions.length) return;
    const deletedId = questions[index].id;
    const newQuestions = questions
      .filter((_, i) => i !== index)
      .map((q, i) => ({
        ...q,
        order: i + 1,
        conditionalRules: (q.conditionalRules || []).filter(
          (r: { condition?: string; targetQuestionId?: string }) => !ruleReferencesQuestionId(r, deletedId)
        ),
      }));
    const routingRules = form.getValues('routingRules') || [];
    const newRoutingRules = routingRules.filter(
      (r: { condition?: string; targetQuestionId?: string }) => !ruleReferencesQuestionId(r, deletedId)
    );
    form.setValue('questions', newQuestions);
    form.setValue('routingRules', newRoutingRules);
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const currentOrder = form.getValues(`questions.${index}.order`);
      const prevOrder = form.getValues(`questions.${index - 1}.order`);
      form.setValue(`questions.${index}.order`, prevOrder);
      form.setValue(`questions.${index - 1}.order`, currentOrder);
      swap(index, index - 1);
    } else if (direction === 'down' && index < fields.length - 1) {
      const currentOrder = form.getValues(`questions.${index}.order`);
      const nextOrder = form.getValues(`questions.${index + 1}.order`);
      form.setValue(`questions.${index}.order`, nextOrder);
      form.setValue(`questions.${index + 1}.order`, currentOrder);
      swap(index, index + 1);
    }
  };

  const questionTypes: QuestionType[] = [
    'SINGLE_CHOICE',
    'MULTIPLE_CHOICE',
    'TEXT_SHORT',
    'TEXT_LONG',
    'NUMBER',
    'EMAIL',
    'DATE',
    'TIME',
    'RATING_SCALE',
    'SLIDER',
    'DROPDOWN',
    'FILE_UPLOAD',
    'BOOLEAN',
    'HEADING',
    'BANNER',
  ];

  const needsOptions = (type: QuestionType) => {
    return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'DROPDOWN'].includes(type);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/surveys">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Survey' : 'Create Survey'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update survey information' : 'Build a new survey'}
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openJsonEditor}>
          <Code className="mr-2 h-4 w-4" />
          Edit in JSON editor
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit as any)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the survey</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control as any}
                name="surveyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Survey ID *</FormLabel>
                    <FormControl>
                      <Input placeholder="SVY-EXAMPLE-001" {...field} />
                    </FormControl>
                    <FormDescription>Pattern: SVY-[a-zA-Z0-9_-]+</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter survey title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter survey description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control as any}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select survey type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="FEEDBACK">Feedback</SelectItem>
                          <SelectItem value="ASSESSMENT">Assessment</SelectItem>
                          <SelectItem value="QUIZ">Quiz</SelectItem>
                          <SelectItem value="POLL">Poll</SelectItem>
                          <SelectItem value="RESEARCH">Research</SelectItem>
                          <SelectItem value="ONBOARDING">Onboarding</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control as any}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="GENERAL">General</SelectItem>
                          <SelectItem value="USER_ONBOARDING">User Onboarding</SelectItem>
                          <SelectItem value="PRODUCT_FEEDBACK">Product Feedback</SelectItem>
                          <SelectItem value="CUSTOMER_SATISFACTION">Customer Satisfaction</SelectItem>
                          <SelectItem value="EMPLOYEE_FEEDBACK">Employee Feedback</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control as any}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tags separated by commas" {...field} />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <FormDescription>Mark this survey as active</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Questions */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Questions ({fields.length})</CardTitle>
                  <CardDescription>Add and configure survey questions</CardDescription>
                </div>
                <Button type="button" onClick={addQuestion}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Question
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {fields.map((field, index) => {
                const questionOptions = form.watch(`questions.${index}.options`) || [];
                const questionType = form.watch(`questions.${index}.type`);
                const needsOpts = needsOptions(questionType);
                const conditionalRules = form.watch(`questions.${index}.conditionalRules`) || [];

                return (
                  <Card key={field.id} className="border-2">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-5 w-5 text-muted-foreground" />
                          <CardTitle className="text-lg">Question {index + 1}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          {fields.length > 1 && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveQuestion(index, 'up')}
                                disabled={index === 0}
                                title="Move up"
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => moveQuestion(index, 'down')}
                                disabled={index === fields.length - 1}
                                title="Move down"
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeQuestion(index)}
                              title="Remove question (and clean references in conditions/routing)"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control as any}
                        name={`questions.${index}.text`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Question Text *</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter question text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 md:grid-cols-2">
                        <FormField
                          control={form.control as any}
                          name={`questions.${index}.type`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Question Type *</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {questionTypes.map((type) => {
                                    const typeLabels: Record<string, string> = {
                                      'SINGLE_CHOICE': 'Single Choice',
                                      'MULTIPLE_CHOICE': 'Multiple Choice',
                                      'TEXT_SHORT': 'Short Text',
                                      'TEXT_LONG': 'Long Text',
                                      'HEADING': 'Heading (Display)',
                                      'BANNER': 'Banner (Overlay)',
                                    };
                                    const label = typeLabels[type] || type.replace(/_/g, ' ');
                                    return (
                                      <SelectItem key={type} value={type}>
                                        {label}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control as any}
                          name={`questions.${index}.order`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Order *</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  {...field}
                                  onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control as any}
                        name={`questions.${index}.helpText`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Help Text</FormLabel>
                            <FormControl>
                              <Input placeholder="Optional help text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control as any}
                        name={`questions.${index}.required`}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Required</FormLabel>
                              <FormDescription>Mark this question as required</FormDescription>
                            </div>
                            <FormControl>
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="h-4 w-4"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      {/* Condition rules: when to show/hide this question */}
                      <div className="space-y-3 border rounded-lg p-4 bg-muted/20">
                        <div className="flex items-center justify-between">
                          <Label className="text-base">Condition rules</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const current = form.getValues(`questions.${index}.conditionalRules`) || [];
                              form.setValue(`questions.${index}.conditionalRules`, [
                                ...current,
                                { condition: '', action: 'SHOW', targetQuestionId: '' },
                              ]);
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add rule
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          e.g. condition: <code className="text-xs bg-muted px-1 rounded">pain_points.includes(&apos;pain_confidence&apos;)</code> — when true, action applies (SHOW/HIDE this question).
                        </p>
                        {conditionalRules.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No condition rules. This question is always shown unless you add a rule.</p>
                        ) : (
                          <div className="space-y-2">
                            {conditionalRules.map((rule: { condition?: string; action?: string; targetQuestionId?: string }, rIdx: number) => (
                              <div key={rIdx} className="flex flex-wrap items-center gap-2 p-3 rounded border bg-background">
                                <Input
                                  placeholder="Condition (e.g. questionId.includes('value'))"
                                  value={rule.condition ?? ''}
                                  onChange={(e) => {
                                    const updated = [...(form.getValues(`questions.${index}.conditionalRules`) || [])];
                                    updated[rIdx] = { ...updated[rIdx], condition: e.target.value };
                                    form.setValue(`questions.${index}.conditionalRules`, updated);
                                  }}
                                  className="flex-1 min-w-[200px] font-mono text-sm"
                                />
                                <Select
                                  value={rule.action ?? 'SHOW'}
                                  onValueChange={(value) => {
                                    const updated = [...(form.getValues(`questions.${index}.conditionalRules`) || [])];
                                    updated[rIdx] = { ...updated[rIdx], action: value };
                                    form.setValue(`questions.${index}.conditionalRules`, updated);
                                  }}
                                >
                                  <SelectTrigger className="w-[120px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="SHOW">SHOW</SelectItem>
                                    <SelectItem value="HIDE">HIDE</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  placeholder="Target question ID (optional)"
                                  value={rule.targetQuestionId ?? ''}
                                  onChange={(e) => {
                                    const updated = [...(form.getValues(`questions.${index}.conditionalRules`) || [])];
                                    updated[rIdx] = { ...updated[rIdx], targetQuestionId: e.target.value };
                                    form.setValue(`questions.${index}.conditionalRules`, updated);
                                  }}
                                  className="w-40 font-mono text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    const updated = (form.getValues(`questions.${index}.conditionalRules`) || []).filter((_: unknown, i: number) => i !== rIdx);
                                    form.setValue(`questions.${index}.conditionalRules`, updated);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Options for choice questions */}
                      {needsOpts && (
                        <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <Label>Options *</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const currentOptions = questionOptions || [];
                                const newOption = {
                                  id: `opt-${Date.now()}-${Math.random()}`,
                                  text: '',
                                  value: '',
                                  order: currentOptions.length + 1,
                                };
                                form.setValue(
                                  `questions.${index}.options`,
                                  [...currentOptions, newOption]
                                );
                              }}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Add Option
                            </Button>
                          </div>
                          {questionOptions.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No options added. Add at least one option for this question type.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {questionOptions.map((option, optIndex) => (
                                <div key={option.id} className="flex items-center gap-2">
                                  <Input
                                    placeholder={`Option ${optIndex + 1}`}
                                    value={option.text}
                                    onChange={(e) => {
                                      const updated = [...questionOptions];
                                      updated[optIndex] = { ...updated[optIndex], text: e.target.value };
                                      form.setValue(`questions.${index}.options`, updated);
                                    }}
                                  />
                                  <Input
                                    placeholder="Value (optional)"
                                    value={option.value || ''}
                                    onChange={(e) => {
                                      const updated = [...questionOptions];
                                      updated[optIndex] = { ...updated[optIndex], value: e.target.value };
                                      form.setValue(`questions.${index}.options`, updated);
                                    }}
                                    className="w-32"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      const updated = questionOptions.filter((_, i) => i !== optIndex);
                                      form.setValue(`questions.${index}.options`, updated);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>

          {/* Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
              <CardDescription>Configure survey behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control as any}
                name="configuration.allowBackNavigation"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Allow Back Navigation</FormLabel>
                      <FormDescription>Allow users to go back to previous questions</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="configuration.showProgressIndicator"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Show Progress Indicator</FormLabel>
                      <FormDescription>Display progress bar during survey</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="configuration.randomizeQuestions"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Randomize Questions</FormLabel>
                      <FormDescription>Show questions in random order</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control as any}
                name="configuration.autoSave"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Auto Save</FormLabel>
                      <FormDescription>Automatically save responses</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={openJsonEditor}>
              <Code className="mr-2 h-4 w-4" />
              Edit in JSON editor
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/surveys">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>

      <Dialog open={jsonEditorOpen} onOpenChange={setJsonEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit survey JSON</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Edit the full survey payload. Apply replaces the form with this content. Ensure at least one question and valid structure.
            </p>
          </DialogHeader>
          <textarea
            className="min-h-[50vh] w-full rounded-md border border-input bg-muted/30 p-3 font-mono text-sm"
            value={jsonEditorValue}
            onChange={(e) => setJsonEditorValue(e.target.value)}
            spellCheck={false}
          />
          {jsonEditorError && (
            <p className="text-sm text-destructive">{jsonEditorError}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setJsonEditorOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={applyJsonEditor}>
              Apply to form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurveyFormPage;
